import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ReviewModal = ({ order, onClose }) => {
  const [reviews, setReviews] = useState(
    (order?.items || []).map(item => ({
      productId: item.productId,
      productName: item.productName,
      rating: 0,
      comment: '',
    }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!order) return null;

  const updateRating = (idx, rating) => {
    setReviews(prev => prev.map((r, i) => i === idx ? { ...r, rating } : r));
  };

  const updateComment = (idx, comment) => {
    setReviews(prev => prev.map((r, i) => i === idx ? { ...r, comment } : r));
  };

  const handleSubmit = async () => {
    const toSubmit = reviews.filter(r => r.rating > 0);
    if (toSubmit.length === 0) {
      toast.error('Please rate at least one item');
      return;
    }
    setSubmitting(true);
    try {
      await Promise.all(toSubmit.map(r =>
        axios.post(`${API}/reviews`, {
          productId: r.productId,
          orderId: order._id,
          customerName: order.customerName,
          rating: r.rating,
          comment: r.comment,
        })
      ));
      setSubmitted(true);
      toast.success('Thank you for your review!');
      setTimeout(onClose, 2000);
    } catch (err) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4" data-testid="review-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#ECE0DA]">
          <h2 className="font-playfair text-lg font-bold text-[#1F1410]">How was your order?</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#FDF8F3] rounded-full transition-colors" data-testid="review-modal-close">
            <X size={18} />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-3">★</div>
            <p className="font-playfair font-semibold text-[#1F1410] text-lg">Thank you!</p>
            <p className="text-[#7A6A63] text-sm mt-1">Your review will be published after approval.</p>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <p className="text-[#7A6A63] text-sm">Rate the items from your order. This helps others discover great food!</p>

            {reviews.map((rev, idx) => (
              <div key={idx} className="border border-[#ECE0DA] rounded-xl p-4">
                <p className="font-semibold text-[#1F1410] text-sm mb-3">{rev.productName}</p>
                {/* Stars */}
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => updateRating(idx, star)}
                      className="transition-transform hover:scale-110"
                      data-testid={`review-star-${idx}-${star}`}
                    >
                      <Star
                        size={24}
                        fill={star <= rev.rating ? '#F59E0B' : 'none'}
                        stroke={star <= rev.rating ? '#F59E0B' : '#D1C4BB'}
                      />
                    </button>
                  ))}
                </div>
                <textarea
                  value={rev.comment}
                  onChange={e => updateComment(idx, e.target.value)}
                  placeholder="Share your experience (optional)"
                  rows={2}
                  className="w-full px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm focus:outline-none focus:border-[#7A1125] resize-none"
                  data-testid={`review-comment-${idx}`}
                />
              </div>
            ))}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 border border-[#ECE0DA] rounded-full text-sm text-[#7A6A63] hover:bg-[#FDF8F3] transition-colors"
                data-testid="review-skip-button"
              >
                Skip
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2.5 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold rounded-full transition-colors"
                data-testid="review-submit-button"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewModal;
